import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Collapse
} from '@mui/material';
import { Edit, Trash2 } from 'react-feather';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import SearchIcon from '@mui/icons-material/Search';
// Keep the others
// ... rest of imports
import DOMPurify from 'dompurify';
import { API_BASE_URL } from '../config/api';
import '../styles/chunks_section.css';

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const validateFileSize = (file) => {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File "${file.name}" is ${formatFileSize(file.size)}. Max size is 10MB.`);
  }
};

// --- NEW COMPONENT: FileList ---
const FileList = ({ files, loading, onDeleteFile, currentUserId }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!files || files.length === 0) {
    return (
      <Box className="kc-empty-chunks">
        <Typography variant="body2">No files have been uploaded yet.</Typography>
      </Box>
    );
  }

  const handleDelete = (file) => {
    onDeleteFile(file.file_id);
  };

  return (
    <List dense className="kc-files-list">
      {files.map((file) => (
        <ListItem key={file.id} className="kc-file-row">
          <Box className="kc-file-main">
            <ListItemIcon className="kc-file-icon">
              <InsertDriveFileIcon />
            </ListItemIcon>
            <ListItemText
              primary={file.file_name}
              secondary={`Size: ${formatFileSize(file.file_size)} | Uploaded: ${new Date(
                file.created_at
              ).toLocaleDateString()}`}
              primaryTypographyProps={{ className: 'kc-file-name' }}
              secondaryTypographyProps={{ className: 'kc-file-meta' }}
            />
          </Box>
          <Box className="kc-file-actions">
            <Tooltip title="Delete file">
              <IconButton size="small" onClick={() => handleDelete(file)} color="error">
                <Trash2 />
              </IconButton>
            </Tooltip>
          </Box>
        </ListItem>
      ))}
    </List>
  );
};
// --- END NEW COMPONENT ---

const FileUpload = ({ onUpload, disabled }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0 && !disabled) {
      try {
        const files = Array.from(e.dataTransfer.files);
        files.forEach(validateFileSize);
        onUpload(files);
      } catch (error) {
        alert(error.message);
      }
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      try {
        const files = Array.from(e.target.files);
        files.forEach(validateFileSize);
        onUpload(files);
      } catch (error) {
        alert(error.message);
      }
    }
  };

  return (
    <Box
      className={`kc-dropzone ${dragActive ? 'drag-active' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input
        type="file"
        multiple
        onChange={handleChange}
        style={{ display: 'none' }}
        id="file-upload"
        accept=".pdf,.txt,.doc,.docx,.md"
        disabled={disabled}
      />
      <label htmlFor="file-upload">
        <Button component="span" variant="contained" disabled={disabled} startIcon={<UploadFileIcon />}>
          Select Files
        </Button>
      </label>
      <Typography variant="body2" sx={{ mt: 1 }}>
        or drag and drop
      </Typography>
      <Typography className="upload-hint">
        PDF, TXT, DOC, DOCX, MD | Max 10MB
      </Typography>
    </Box>
  );
};

const createFlowingText = (content) => {
  if (!content) return 'No content available';

  let text = content;

  // Remove all HTML tags
  text = text.replace(/<[^>]*>/g, '');

  // Decode HTML entities
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&#39;/g, "'");

  // Replace all types of line breaks with spaces
  text = text.replace(/\r?\n/g, ' ');
  text = text.replace(/\r/g, ' ');

  // Clean up multiple spaces
  text = text.replace(/\s+/g, ' ');

  // Trim whitespace
  return text.trim();
};

const ChunkCard = ({
  chunk,
  index,
  onEdit,
  onDelete,
  currentUserId,
  setChunks,
  userPlan,
  knowledgebaseId,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [tagsValue, setTagsValue] = useState('');
  const [tagsSaveSuccess, setTagsSaveSuccess] = useState(false);

  const flowingText = DOMPurify.sanitize(chunk.text || '', { USE_PROFILES: { html: false } });
  const displayContent = expanded
    ? flowingText
    : flowingText.substring(0, 200) + (flowingText.length > 200 ? '...' : '');

  useEffect(() => {
    if (chunk.tag_set && Array.isArray(chunk.tag_set)) {
      setTagsValue(chunk.tag_set.join(', '));
    } else {
      setTagsValue('');
    }
  }, [chunk.tag_set]);

  const handleEdit = () => {
    onEdit(chunk);
  };

  const handleDelete = () => {
    onDelete(chunk.id);
  };

  const handleTagsSave = async () => {
    if (!currentUserId) return;

    // Convert comma-separated string back to array
    const newTags = tagsValue
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);

    try {
      const fd = new FormData();
      fd.append('chunk_id', chunk.chunk_id);
      fd.append(
        'content',
        JSON.stringify({
          content: chunk.text ?? '',
          tags: newTags ?? [],
          metadata: chunk.metadata,
        })
      );

      const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgebaseId}/edit`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: fd,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status}`);
      }

      // Update local chunk state to reflect the changes
      setChunks((prevChunks) =>
        prevChunks.map((c) => (c.id === chunk.chunk_id ? { ...c, tag_set: newTags } : c))
      );

      // Show success notification
      setTagsSaveSuccess(true);
      setTimeout(() => setTagsSaveSuccess(false), 2000);
    } catch (error) {
      console.error('Error saving tags:', error);
      // Reset to original value on error
      if (chunk.tag_set && Array.isArray(chunk.tag_set)) {
        setTagsValue(chunk.tag_set.join(', '));
      } else {
        setTagsValue('');
      }
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
      handleTagsSave(); // Save the tags
    }
  };

  return (
    <Card className="chunk-card">
      <CardContent>
        <Box className="chunk-card-header">
          <Box className="chunk-card-title">
            <Typography variant="h6" component="div">
              Chunk #{index + 1}
            </Typography>
            <Chip
              label={`Score: ${chunk.score.toFixed(2)}`}
              size="small"
              variant="outlined"
              className="chunk-score-pill"
            />
          </Box>
          <Box className="chunk-card-actions">
            <Tooltip title="Edit chunk">
              <IconButton size="small" onClick={handleEdit} color="primary">
                <Edit />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete chunk">
              <IconButton size="small" onClick={handleDelete} color="error">
                <Trash2 />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography variant="body2" className="chunk-id">
          ID: {chunk.chunk_id}
        </Typography>

        <Typography variant="body2" className="chunk-tags">
            <div className='chunk-tags-label'> Tags:</div>
        <Box className="chunk-tags-input-wrapper">
        <TextField
            fullWidth
            size="small"
            value={tagsValue}
            onChange={(e) => setTagsValue(e.target.value)}
            placeholder="Enter tags separated by commas"
            variant="outlined"
            onBlur={handleTagsSave}
            onKeyPress={handleKeyPress}
            disabled={userPlan === 'lite monthly' || userPlan === 'lite annually'}
          />
        </Box>
        </Typography>
        

        {tagsSaveSuccess && <Box className="chunk-tags-success">✓ Changes saved successfully</Box>}

        <Typography variant="body2" className="chunk-body">
          {displayContent}
        </Typography>

        {flowingText.length > 200 && (
          <Box className="chunk-expand-row">
            <Button
              size="small"
              onClick={() => setExpanded(!expanded)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {expanded ? 'Show Less' : 'Show More'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const ChunksList = ({
  chunks,
  loading,
  searchQuery,
  onEditChunk,
  onDeleteChunk,
  setChunks,
  currentUserId,
  knowledgebaseId,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!chunks || chunks.length === 0) {
    return (
      <Box className="kc-empty-chunks">
        <TextSnippetIcon sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h6">No Chunks Found</Typography>
        <Typography variant="body2">
          {searchQuery ? `No results for "${searchQuery}".` : 'Your knowledge base is empty. Upload content to begin.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" className="kc-chunks-summary">
        {searchQuery
          ? `Found ${chunks.length} results`
          : `Showing the ${chunks.length} most recent chunks`}
      </Typography>
      {chunks.map((chunk, index) => (
        <ChunkCard
          key={chunk.chunk_id}
          chunk={chunk}
          index={index}
          loading={loading}
          searchQuery={searchQuery}
          onEdit={onEditChunk}
          onDelete={onDeleteChunk}
          setChunks={setChunks}
          currentUserId={currentUserId}
          knowledgebaseId={knowledgebaseId}
        />
      ))}
    </Box>
  );
};

const Chunkssection = ({ knowledge_id, user_id, kb_name, tool_id, knowledge_config, onSave }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [textContent, setTextContent] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [chunks, setChunks] = useState([]);
  const [chunksLoading, setChunksLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingChunk, setEditingChunk] = useState(null);
  const [editedText, setEditedText] = useState('');
  const currentUserId = user_id;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingChunkId, setDeletingChunkId] = useState(null);
  const [deleteFileDialogOpen, setFileDeleteDialogOpen] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [score, setScore] = useState(0.1);
  const [scoreInput, setScoreInput] = useState('0.1'); // <--- ADD THIS
  const [searchType, setSearchType] = useState('hybrid');
  const [pageSize, setPageSize] = useState(10);
  const CHUNK_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);
  const [crawlText, setCrawlText] = useState('');
  const [crawlLoading, setCrawlLoading] = useState(false);

  const knowledgeId = useMemo(() => {
    if (knowledge_id === null || knowledge_id === undefined || knowledge_id === '') {
      return null;
    }
    return String(knowledge_id);
  }, [knowledge_id]);

  const datasetId = useMemo(() => {
    return knowledge_config?.dataset_id || null;
  }, [knowledge_config]);

  useEffect(() => {
    if (!knowledge_config) return;

    const kc = knowledge_config || {};
    if (typeof kc.score === 'number') {
          setScore(kc.score);            // Update logical value
          setScoreInput(String(kc.score)); // Update text field value
        }    
    if (typeof kc.search_type === 'string') setSearchType(kc.search_type);
    if (typeof kc.page_size === 'number') setPageSize(kc.page_size);
    if (typeof kc.is_hidden === 'boolean') {
      setIsHidden(kc.is_hidden);
    }
  }, [knowledge_config]);

  const handleSaveSettings = async () => {
    if (!datasetId) {
      setError('Dataset ID is missing. Cannot save settings.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');

    const payload = {
      name: kb_name,
      configuration: {
        provisioner_keys: [],
        set_keys: {
          dataset_id: datasetId,
          score: Number.isFinite(score) ? score : 0.1,
          search_type: searchType,
          page_size: Number.isInteger(pageSize) ? pageSize : 10,
          get_total_pages: false,
          slim_chunks: false,
          knowledge_embedding: 'text-embedding-3-small',
          is_hidden: isHidden,
        },
        secrets: [],
      },
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/tools/${tool_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || `Request failed: ${res.status}`);
      }
      setSuccess('Knowledge tool settings saved.');
      if (onSave) {
        onSave({
          score: Number.isFinite(score) ? score : 0.1,
          search_type: searchType,
          page_size: Number.isInteger(pageSize) ? pageSize : 10,
          is_hidden: isHidden,
        });
      }
    } catch (e) {
      setError(e?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const searchKnowledge = useCallback(
    async (query, top_k = 10) => {
      if (!knowledgeId) {
        setChunks([]);
        setChunksLoading(false);
        setSearchLoading(false);
        return;
      }

      setChunksLoading(true);
      setSearchLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgeId}/search`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query, top_k }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `Server error: ${response.status}`);
        }

        const data = await response.json();
        setChunks(data);
      } catch (err) {
        console.error('Failed to search knowledge:', err);
        setError('Failed to fetch knowledge chunks.');
        setChunks([]);
      } finally {
        setChunksLoading(false);
        setSearchLoading(false);
      }
    },
    [knowledgeId]
  );

  const fetchFiles = useCallback(async () => {
    if (!knowledgeId) {
      setFiles([]);
      setFilesLoading(false);
      return;
    }

    setFilesLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgeId}/files`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      const data = await response.json();
      setFiles(data);
    } catch (err) {
      console.error('Failed to fetch files:', err);
      setError('Failed to fetch the list of uploaded files.');
    } finally {
      setFilesLoading(false);
    }
  }, [knowledgeId]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery) {
        searchKnowledge(searchQuery);
      } else {
        searchKnowledge('*', 5);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchQuery, searchKnowledge]);

  const handleUpload = async (data) => {
    if (!knowledgeId) {
      setError('Knowledge base ID is missing. Cannot upload content.');
      return false;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgeId}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        body: data,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status}`);
      }

      setSuccess('Content uploaded successfully. It will be available for search shortly.');
      searchKnowledge('*', 5);
      fetchFiles();
      return true;
    } catch (err) {
      console.error('Upload error:', err);
      setError(`Upload failed: ${err.message}`);
      return false;
    } finally {
      setUploading(false);
      setTimeout(() => setSuccess(''), 5000);
    }
  };

  const handleFileUpload = (files) => {
    if (!files || files.length === 0) return;
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    handleUpload(formData);
  };

  const handleTextUpload = async () => {
    if (!textContent.trim()) return;
    const formData = new FormData();
    formData.append('content', textContent);
    const uploadSuccessful = await handleUpload(formData);
    if (uploadSuccessful) {
      setTextContent('');
    }
  };

  const handleEditChunk = (chunk) => {
    setEditingChunk(chunk);
    setEditedText(createFlowingText(chunk.text));
    setEditDialogOpen(true);
  };

  const handleSaveEditedChunk = async () => {
    if (!editingChunk || !currentUserId) return;

    setError('');
    try {
      const fd = new FormData();
      fd.append('chunk_id', String(editingChunk.chunk_id));
      fd.append(
        'content',
        JSON.stringify({
          content: editedText ?? '',
          link: editingChunk.link ?? null,
          tags: editingChunk.tag_set ?? [],
          metadata: editingChunk.metadata,
        })
      );
      const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgeId}/edit`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: fd,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status}`);
      }

      setChunks((prevChunks) =>
        prevChunks.map((chunk) =>
          chunk.chunk_id === editingChunk.chunk_id ? { ...chunk, text: editedText } : chunk
        )
      );

      setSuccess('Chunk updated successfully and will take some time to reflect changes in the database');
      setEditDialogOpen(false);
      setEditingChunk(null);
      setEditedText('');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error updating chunk:', error);
      setError('Failed to update chunk: ' + error.message);
    }
  };

  const handleDeleteChunk = (chunkId) => {
    setDeletingChunkId(chunkId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteChunk = async () => {
    if (!deletingChunkId || !currentUserId) return;

    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgeId}/delete`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ chunk_id: deletingChunkId }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status}`);
      }

      setChunks((prevChunks) =>
        prevChunks.filter((chunk) => chunk.chunk_id !== deletingChunkId)
      );

      setSuccess('Chunk deleted successfully');
      setDeleteDialogOpen(false);
      setDeletingChunkId(null);
      searchKnowledge('*', 5);
      fetchFiles();
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting chunk:', error);
      setError('Failed to delete chunk: ' + error.message);
    }
  };
  const handleCrawlSubmit = async () => {
    setError('');
    setSuccess('');

    if (!knowledgeId) {
      setError('Knowledge base ID is missing. Cannot start crawl.');
      return;
    }

    // Split by newline, trim, and filter empty lines
    const rawLines = crawlText.split('\n');
    let urls = rawLines
      .map(line => line.trim())
      .filter(line => line.length > 0);

    if (urls.length === 0) {
      setError('Please enter at least one website URL.');
      return;
    }

    // Simple normalization: add https:// if no scheme
    urls = urls.map((u) => {
      if (!/^https?:\/\//i.test(u)) {
        return `https://${u}`;
      }
      return u;
    });

    setCrawlLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgeId}/crawl`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({ urls }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status}`);
      }

      const data = await response.json().catch(() => ({}));

      setSuccess(
        data?.message ||
          `Started crawling ${urls.length} URL${urls.length > 1 ? 's' : ''}. Content will appear once processed.`
      );
      setCrawlText('');
    } catch (err) {
      console.error('Crawl error:', err);
      setError(`Failed to start crawl: ${err.message}`);
    } finally {
      setCrawlLoading(false);
      setTimeout(() => setSuccess(''), 5000);
    }
  };

  const handleDeleteFile = (fileId) => {
    setDeletingFileId(fileId);
    setFileDeleteDialogOpen(true);
  };

  const handleConfirmDeleteFile = async () => {
    if (!deletingFileId || !currentUserId) return;

    setError('');
    setIsDeleting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/knowledge/${knowledgeId}/delete`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_id: String(deletingFileId) }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Server error: ${response.status}`);
      }

      await fetchFiles();
      searchKnowledge('*', 5);
      setSuccess('File deleted successfully');
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Error deleting file:', error);
      setError('Failed to delete file: ' + error.message);
    } finally {
      setDeletingFileId(null);
      setIsDeleting(false);
      setFileDeleteDialogOpen(false);
    }
  };

  return (
    <Box className="knowledge-chunks-root">
      {error && (
        <Box className="kc-alert-row">
          <Alert
            severity="error"
            onClose={() => setError('')}
            className="kc-alert error"
          >
            {error}
          </Alert>
        </Box>
      )}
      {success && (
        <Box className="kc-alert-row">
          <Alert
            severity="success"
            onClose={() => setSuccess('')}
            className="kc-alert success"
          >
            {success}
          </Alert>
        </Box>
      )}

      {/* Upload Knowledge */}
      <Paper className="knowledge-chunks-card kc-upload-card">
        <Box className="kc-upload-title">
          <Typography variant="h6" gutterBottom>
            Upload Knowledge
          </Typography>
        </Box>
        <FileUpload onUpload={handleFileUpload} disabled={uploading} />
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom className="kc-files-header">
          Uploaded Files
        </Typography>
        <FileList
          files={files}
          loading={filesLoading}
          onDeleteFile={handleDeleteFile}
          currentUserId={currentUserId}
        />
      </Paper>

      {/* Add Text Content */}
      <Paper className="knowledge-chunks-card kc-text-card">
        <Typography variant="h6" gutterBottom>
          Add Text Content
        </Typography>
        <Box className="kc-textarea-wrapper">
          <TextField
            fullWidth
            multiline
            rows={5}
            placeholder="Paste text content here..."
            value={textContent}
            onChange={(e) => setTextContent(e.target.value)}
            disabled={uploading}
          />
        </Box>
        <Box className="kc-text-button-row">
          <Button
            variant="contained"
            onClick={handleTextUpload}
            disabled={uploading || !textContent.trim()}
          >
            {uploading ? <CircularProgress size={24} /> : 'Add Text'}
          </Button>
        </Box>
      </Paper>
      {/* Crawl Websites */}
        <Paper className="knowledge-chunks-card kc-crawl-card">
          <Typography variant="h6" gutterBottom>
            Crawl Websites
          </Typography>

          <Box className="kc-textarea-wrapper">
            <TextField
              fullWidth
              multiline
              rows={5}
              placeholder={`Enter one website URL per line:\nhttps://example.com\nhttps://docs.example.com`}
              value={crawlText}
              onChange={(e) => setCrawlText(e.target.value)}
              disabled={crawlLoading || uploading}
            />
          </Box>

          <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
            URLs will be split by line breaks (<code>\n</code>), normalized, and sent to the crawler.
          </Typography>

          <Box className="kc-text-button-row">
            <Button
              variant="contained"
              onClick={handleCrawlSubmit}
              disabled={crawlLoading || uploading || !crawlText.trim()}
            >
              {crawlLoading ? <CircularProgress size={24} /> : 'Start Crawl'}
            </Button>
          </Box>
        </Paper>

      {/* Search + Chunks */}
      <Paper className="knowledge-chunks-card kc-search-card">
        <Typography variant="h6" gutterBottom>
          Knowledge Base Search
        </Typography>
        <Box className="kc-search-input-row">
          <TextField
            fullWidth
            placeholder="Search knowledge..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <ChunksList
          chunks={chunks}
          loading={chunksLoading || searchLoading}
          searchQuery={searchQuery}
          onEditChunk={handleEditChunk}
          onDeleteChunk={handleDeleteChunk}
          setChunks={setChunks}
          currentUserId={currentUserId}
          knowledgebaseId={knowledgeId}
        />
      </Paper>

      {/* Advanced Options */}
      <Paper className="kc-advanced-card">
        <Box
          className="kc-advanced-header"
          onClick={() => setAdvancedOpen((o) => !o)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setAdvancedOpen((o) => !o);
            }
          }}
        >
          <Typography variant="h6" gutterBottom={false}>
            Advance Options
          </Typography>
          <IconButton
            className="kc-advanced-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setAdvancedOpen((o) => !o);
            }}
            aria-expanded={advancedOpen}
            aria-controls="advanced-options-content"
            size="small"
          >
            <ExpandMoreIcon
              sx={{
                transform: advancedOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          </IconButton>
        </Box>

        <Collapse in={advancedOpen}>
          {!datasetId && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Dataset ID is not set; saving will be disabled.
            </Alert>
          )}

          <Box id="advanced-options-content" className="kc-advanced-grid">
            <TextField
              label="Score (0–1)"
              type="number"
              
              // Bind to the STRING state to allow "0." typing
              value={scoreInput} 
              
              onChange={(e) => {
                const v = e.target.value;
                setScoreInput(v); // Update UI immediately so typing isn't blocked

                // Parse for logic
                const n = parseFloat(v);
                
                // Only update the actual 'score' payload if it's a valid number
                if (!isNaN(n) && n >= 0 && n <= 1) {
                  setScore(n);
                  setError('');
                } else if (v === '') {
                   // Handle empty case if needed, or leave score as is
                } else {
                  setError("Enter a number between 0 and 1");
                }
              }}
              onWheel={(e) => e.currentTarget.blur()}
              
              // FIX: Set step to "any" or extremely small to allow 0.001
              inputProps={{ min: 0, max: 1, step: "0.0001", inputMode: 'decimal' }} 
              
              error={Boolean(error)}
              helperText={error || 'Enter a number between 0 and 1'}
              fullWidth
            />
            <TextField
              select
              label="Search type"
              SelectProps={{ native: true }}
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              fullWidth
            >
              <option value="hybrid">hybrid</option>
              <option value="semantic">semantic</option>
              <option value="keyword">keyword</option>
            </TextField>

            <TextField
              select
              label="Max number of chunks (1–10)"
              SelectProps={{ native: true }}
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              fullWidth
            >
              {CHUNK_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </TextField>
          </Box>
          <Box className="kc-advanced-footer">
            <Button
              variant="contained"
              onClick={handleSaveSettings}
              disabled={saving || !datasetId}
            >
              {saving ? <CircularProgress size={20} /> : 'Save settings'}
            </Button>
          </Box>
        </Collapse>
      </Paper>

      {/* Edit Chunk Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle className="kc-dialog-title">Edit Chunk</DialogTitle>
        <DialogContent>
          <DialogContentText className="kc-dialog-text" sx={{ mb: 2 }}>
            Edit the content of this chunk. The text will be updated in your knowledge base.
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={10}
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            placeholder="Enter chunk content..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEditedChunk}
            variant="contained"
            disabled={!editedText.trim()}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Chunk Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle className="kc-dialog-title">Delete Chunk</DialogTitle>
        <DialogContent>
          <DialogContentText className="kc-dialog-text">
            Are you sure you want to delete this chunk? This action cannot be undone and will remove
            the content from your knowledge base.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDeleteChunk} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete File Dialog */}
      <Dialog open={deleteFileDialogOpen} onClose={() => setFileDeleteDialogOpen(false)}>
        <DialogTitle className="kc-dialog-title">Delete File</DialogTitle>
        <DialogContent>
          <DialogContentText className="kc-dialog-text">
            Are you sure you want to delete this file? This action cannot be undone and will remove
            the file as well as contents from your knowledge base.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFileDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDeleteFile} color="error" disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Chunkssection;
